import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Pill, Plus, X, Check, Loader2, ChevronDown, Edit3,
  Pause, Play, AlertTriangle, Clock, Search, History
} from 'lucide-react';
import { api } from '../../services/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
  discontinued: 'bg-slate-100 text-slate-400 border-slate-200',
};

const FORM_LABELS = {
  tablet: '💊', capsule: '💊', liquid: '🧴', cream: '🧴', ointment: '🧴',
  injection: '💉', inhaler: '🫁', patch: '🩹', drops: '💧', spray: '🌬️',
};

export default function MedsContent({ medications: legacyMeds, patientId }) {
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  const [expandedMed, setExpandedMed] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [showRxPad, setShowRxPad] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [showRxHistory, setShowRxHistory] = useState(false);

  const fetchMeds = useCallback(() => {
    if (!patientId) { setLoading(false); return; }
    api.get(`/patients/${patientId}/active-medications?include_discontinued=${showDiscontinued}`)
      .then(r => setMeds(r.data.medications || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [patientId, showDiscontinued]);

  useEffect(() => { fetchMeds(); }, [fetchMeds]);

  const activeMeds = meds.filter(m => m.status === 'active');
  const heldMeds = meds.filter(m => m.status === 'on_hold');
  const discMeds = meds.filter(m => m.status === 'discontinued');

  const [showMenu, setShowMenu] = useState(false);
  const [pendingRx, setPendingRx] = useState([]);

  // Load pending/draft prescriptions
  useEffect(() => {
    if (!patientId) return;
    api.get(`/patients/${patientId}/prescriptions?status=draft`).then(r => setPendingRx(r.data.prescriptions || [])).catch(() => {});
  }, [patientId, showRxPad]);

  const loadAudit = async () => {
    try {
      const res = await api.get(`/patients/${patientId}/medication-audit`);
      setAuditEntries(res.data.entries || []);
      setShowAudit(true);
    } catch { toast.error('Failed to load audit'); }
  };

  const loadPrescriptions = async () => {
    try {
      const res = await api.get(`/patients/${patientId}/prescriptions`);
      setPrescriptions(res.data.prescriptions || []);
      setShowRxHistory(true);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;

  return (
    <div className="space-y-4" data-testid="meds-content">
      {/* Header — decluttered */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>Medications</h2>
          <Badge variant="outline" className="text-[10px]">{activeMeds.length} active</Badge>
          {heldMeds.length > 0 && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200">{heldMeds.length} on hold</Badge>}
          {pendingRx.length > 0 && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">{pendingRx.length} pending</Badge>}
        </div>
        <div className="flex items-center gap-1.5">
          {/* More menu — icon dropdown */}
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="med-more-menu">
              <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
            </button>
            {showMenu && (
              <div className="absolute right-0 top-8 w-44 bg-white rounded-xl border shadow-lg z-20 py-1 animate-in fade-in-0 slide-in-from-top-1 duration-100"
                data-testid="med-menu-dropdown">
                <button onClick={() => { loadPrescriptions(); setShowMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2">
                  <History className="w-3.5 h-3.5 text-slate-400" /> Rx History
                </button>
                <button onClick={() => { loadAudit(); setShowMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2">
                  <History className="w-3.5 h-3.5 text-slate-400" /> Audit Log
                </button>
                <button onClick={() => { setShowDiscontinued(!showDiscontinued); setShowMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-slate-400" /> {showDiscontinued ? 'Hide' : 'Show'} Discontinued
                </button>
              </div>
            )}
          </div>
          {/* Primary actions */}
          <Button size="sm" className="h-7 gap-1 text-xs rounded-full" onClick={() => { setShowRxPad(!showRxPad); setShowAdd(false); }}
            data-testid="write-rx-btn">
            {showRxPad ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
            {showRxPad ? 'Close' : 'Write Rx / Add Med'}
          </Button>
        </div>
      </div>

      {/* Rx Pad (unified — both Write Rx and Add Med) */}
      {showRxPad && <RxPadForm patientId={patientId} onWritten={() => { setShowRxPad(false); fetchMeds(); setPendingRx([]); }} onClose={() => setShowRxPad(false)} />}

      {/* Pending/Draft Prescriptions */}
      {pendingRx.length > 0 && !showRxPad && (
        <PendingRxSection pendingRx={pendingRx} patientId={patientId} onChanged={() => {
          api.get(`/patients/${patientId}/prescriptions?status=draft`).then(r => setPendingRx(r.data.prescriptions || []));
          fetchMeds();
        }} />
      )}

      {/* Rx History */}
      {showRxHistory && (
        <RxHistoryPanel prescriptions={prescriptions} onClose={() => setShowRxHistory(false)} patientId={patientId} />
      )}

      {/* Active medications */}
      {activeMeds.length === 0 && heldMeds.length === 0 && !showDiscontinued && (
        <div className="text-center py-12">
          <Pill className="w-8 h-8 mx-auto mb-2 text-slate-200" />
          <p className="text-sm text-slate-400">No active medications</p>
          <p className="text-xs text-slate-300 mt-1">Click "Add Medication" to start</p>
        </div>
      )}

      {/* On Hold section */}
      {heldMeds.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Pause className="w-3 h-3" /> On Hold ({heldMeds.length})
          </p>
          <div className="space-y-1.5">
            {heldMeds.map(med => <MedRow key={med.id} med={med} expanded={expandedMed === med.id}
              onToggle={() => setExpandedMed(expandedMed === med.id ? null : med.id)}
              patientId={patientId} onChanged={fetchMeds} />)}
          </div>
        </div>
      )}

      {/* Active section */}
      {activeMeds.length > 0 && (
        <div className="space-y-1.5">
          {activeMeds.map(med => <MedRow key={med.id} med={med} expanded={expandedMed === med.id}
            onToggle={() => setExpandedMed(expandedMed === med.id ? null : med.id)}
            patientId={patientId} onChanged={fetchMeds} />)}
        </div>
      )}

      {/* Discontinued section */}
      {showDiscontinued && discMeds.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Discontinued ({discMeds.length})
          </p>
          <div className="space-y-1 opacity-60">
            {discMeds.map(med => <MedRow key={med.id} med={med} expanded={expandedMed === med.id}
              onToggle={() => setExpandedMed(expandedMed === med.id ? null : med.id)}
              patientId={patientId} onChanged={fetchMeds} />)}
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAudit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowAudit(false)}>
          <div className="absolute inset-0 bg-black/15" />
          <div className="relative w-full max-w-lg max-h-[70vh] bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between shrink-0">
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Medication Audit Log</h3>
              <button onClick={() => setShowAudit(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {auditEntries.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No audit entries</p>
              ) : auditEntries.map(entry => (
                <div key={entry.id} className="text-xs border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[8px]">{entry.action_type?.replace(/_/g, ' ')}</Badge>
                    <span className="text-slate-500">{entry.user_name}</span>
                    <span className="text-slate-300 ml-auto">{new Date(entry.timestamp).toLocaleString()}</span>
                  </div>
                  {entry.new_value && <p className="text-slate-600 mt-0.5">{JSON.stringify(entry.new_value)}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function MedRow({ med, expanded, onToggle, patientId, onChanged }) {
  const formIcon = FORM_LABELS[med.form] || '💊';
  const statusClass = STATUS_COLORS[med.status] || STATUS_COLORS.active;

  return (
    <div className={`rounded-xl border bg-white overflow-hidden transition-all ${med.status === 'on_hold' ? 'border-amber-200' : 'border-slate-200'}`}
      data-testid={`med-row-${med.id}`}>
      <button onClick={onToggle} className="w-full text-left px-4 py-3 flex items-center gap-3 group hover:bg-slate-50/50 transition-colors">
        <span className="text-base">{formIcon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-700 truncate">{med.medication_name}</span>
            <Badge variant="outline" className={`text-[8px] h-4 ${statusClass}`}>{med.status?.replace('_', ' ')}</Badge>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {med.dose} {med.form} · {med.route} · {med.frequency || med.sig || 'as directed'}
          </p>
        </div>
        {med.linked_diagnosis && <Badge variant="outline" className="text-[8px] font-mono shrink-0">{med.linked_diagnosis}</Badge>}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-300 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <MedDetail med={med} patientId={patientId} onChanged={onChanged} />
      )}
    </div>
  );
}


function MedDetail({ med, patientId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDiscontinue, setShowDiscontinue] = useState(false);
  const [dcReason, setDcReason] = useState('');
  const [dcDetail, setDcDetail] = useState('');

  const fields = [
    { label: 'Dose', value: med.dose },
    { label: 'Form', value: med.form },
    { label: 'Route', value: med.route },
    { label: 'Frequency', value: med.frequency },
    { label: 'Sig', value: med.sig },
    { label: 'Quantity', value: med.quantity },
    { label: 'Refills', value: `${med.refills_remaining || 0} of ${med.refills_total || 0}` },
    { label: 'Pharmacy', value: med.pharmacy_name || '—' },
    { label: 'Diagnosis', value: med.linked_diagnosis },
    { label: 'Prescriber', value: med.prescriber_name },
    { label: 'Start Date', value: med.start_date },
  ];

  return (
    <div className="px-4 pb-3 border-t border-slate-100">
      {/* Detail grid */}
      <div className="grid grid-cols-3 gap-x-4 gap-y-1.5 mt-2">
        {fields.map(f => f.value && (
          <div key={f.label}>
            <p className="text-[9px] text-slate-400 uppercase tracking-wider">{f.label}</p>
            <p className="text-xs text-slate-700">{f.value}</p>
          </div>
        ))}
      </div>

      {med.notes && <p className="text-xs text-slate-500 mt-2 italic">{med.notes}</p>}

      {/* Actions */}
      {med.status !== 'discontinued' && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-2 border-t border-slate-100">
          {med.status === 'active' && (
            <>
              <Button size="sm" variant="ghost" className="h-6 text-[9px] gap-0.5 rounded-full text-slate-500"
                onClick={async () => {
                  const reason = prompt('Hold reason:');
                  if (!reason) return;
                  await api.post(`/patients/${patientId}/active-medications/${med.id}/hold`, { reason, resume_date: '' });
                  toast.success('Medication placed on hold'); onChanged();
                }} data-testid={`hold-${med.id}`}>
                <Pause className="w-2.5 h-2.5" /> Hold
              </Button>
              <Button size="sm" variant="ghost" className="h-6 text-[9px] gap-0.5 rounded-full text-slate-500"
                onClick={() => { setEditing(true); setEditForm({ dose: med.dose, frequency: med.frequency, sig: med.sig, quantity: med.quantity }); }}>
                <Edit3 className="w-2.5 h-2.5" /> Edit
              </Button>
            </>
          )}
          {med.status === 'on_hold' && (
            <Button size="sm" variant="ghost" className="h-6 text-[9px] gap-0.5 rounded-full text-emerald-600"
              onClick={async () => {
                await api.post(`/patients/${patientId}/active-medications/${med.id}/resume`);
                toast.success('Medication resumed'); onChanged();
              }}>
              <Play className="w-2.5 h-2.5" /> Resume
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-6 text-[9px] gap-0.5 rounded-full text-red-500"
            onClick={() => setShowDiscontinue(!showDiscontinue)}>
            <X className="w-2.5 h-2.5" /> Discontinue
          </Button>
        </div>
      )}

      {/* Discontinue form */}
      {showDiscontinue && (
        <div className="mt-2 p-3 rounded-lg bg-red-50 border border-red-200 space-y-2">
          <p className="text-xs font-medium text-red-700">Discontinue {med.medication_name}</p>
          <select value={dcReason} onChange={e => setDcReason(e.target.value)} className="w-full text-xs border rounded px-2 py-1.5">
            <option value="">Select reason...</option>
            {['completed_course', 'side_effects', 'allergy', 'ineffective', 'patient_request', 'drug_interaction', 'cost', 'provider_decision', 'replaced_by_alternative', 'no_longer_indicated', 'other'].map(r =>
              <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
            )}
          </select>
          <input type="text" placeholder="Additional detail..." value={dcDetail} onChange={e => setDcDetail(e.target.value)}
            className="w-full text-xs border rounded px-2 py-1.5" />
          <div className="flex gap-1.5">
            <Button size="sm" variant="destructive" className="h-7 text-[10px] gap-1" disabled={!dcReason}
              onClick={async () => {
                const res = await api.post(`/patients/${patientId}/active-medications/${med.id}/discontinue`, { reason: dcReason, reason_detail: dcDetail });
                toast.success('Medication discontinued');
                if (res.data.allergy_suggestion) {
                  if (confirm(`${res.data.allergy_suggestion.message}. Add to allergy list?`)) {
                    await api.post(`/patients/${patientId}/active-medications/${med.id}/add-allergy`, {
                      reaction: dcDetail || 'Adverse reaction', severity: 'moderate',
                    });
                    toast.success('Added to allergy list');
                  }
                }
                onChanged();
              }}>
              <X className="w-3 h-3" /> Confirm Discontinue
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setShowDiscontinue(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div className="mt-2 p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {[{ key: 'dose', label: 'Dose' }, { key: 'frequency', label: 'Frequency' }, { key: 'sig', label: 'Sig' }, { key: 'quantity', label: 'Quantity' }].map(f => (
              <div key={f.key}>
                <label className="text-[9px] text-slate-500 uppercase">{f.label}</label>
                <input value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                  className="w-full text-xs border rounded px-2 py-1.5" />
              </div>
            ))}
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 text-[10px] gap-1" onClick={async () => {
              await api.put(`/patients/${patientId}/active-medications/${med.id}`, editForm);
              toast.success('Updated'); setEditing(false); onChanged();
            }}><Check className="w-3 h-3" /> Save</Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}


function AddMedicationForm({ patientId, onAdded, onClose }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ dose: '', form: 'tablet', route: 'oral', frequency: '', sig: '', quantity: '', refills_total: '0', linked_diagnosis: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    api.get(`/medications/search?q=${search}&limit=8`).then(r => setResults(r.data.medications || [])).catch(() => {});
  }, [search]);

  const selectMed = async (med) => {
    setSelected(med);
    setSearch(''); setResults([]);
    try {
      const res = await api.get(`/medications/${encodeURIComponent(med.name)}/defaults`);
      const d = res.data.defaults;
      setForm(f => ({
        ...f,
        dose: d.default_dose || med.strengths?.[0] || '',
        form: d.form || 'tablet',
        route: d.route || 'oral',
        frequency: d.default_frequency || '',
        sig: d.default_sig || '',
        quantity: d.default_quantity?.toString() || '30',
        refills_total: d.default_refills?.toString() || '0',
      }));
    } catch {}
  };

  const handleSubmit = async () => {
    if (!selected || !form.linked_diagnosis) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/patients/${patientId}/active-medications`, {
        medication_name: selected.name, ...form, refills_total: parseInt(form.refills_total) || 0,
      });
      if (res.data.allergy_warning?.match) {
        toast.error(res.data.allergy_warning.message, { duration: 8000 });
      }
      toast.success(`${selected.name} added`);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-white p-4 space-y-3" data-testid="add-med-form">
      {/* Search */}
      {!selected ? (
        <div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search medication..." autoFocus
              className="w-full pl-10 pr-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50" data-testid="med-search" />
          </div>
          {results.length > 0 && (
            <div className="border rounded-xl mt-1.5 max-h-48 overflow-y-auto shadow-sm">
              {results.map(med => (
                <button key={med.name} onClick={() => selectMed(med)}
                  className="w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50 border-b last:border-0 flex items-center gap-2">
                  <Pill className="w-3.5 h-3.5 text-slate-400" />
                  <div>
                    <span className="font-medium text-slate-700">{med.name}</span>
                    {med.strengths?.length > 0 && <span className="text-slate-400 ml-1">({med.strengths.join(', ')})</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Pill className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-semibold text-slate-700">{selected.name}</span>
            <button onClick={() => setSelected(null)} className="text-[9px] text-blue-500">change</button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] text-slate-500 uppercase">Dose</label>
              <input value={form.dose} onChange={e => setForm(f => ({ ...f, dose: e.target.value }))} className="w-full text-xs border rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="text-[9px] text-slate-500 uppercase">Form</label>
              <select value={form.form} onChange={e => setForm(f => ({ ...f, form: e.target.value }))} className="w-full text-xs border rounded px-2 py-1.5">
                {['tablet','capsule','liquid','cream','ointment','gel','patch','injection','inhaler','drops','spray','solution'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] text-slate-500 uppercase">Route</label>
              <select value={form.route} onChange={e => setForm(f => ({ ...f, route: e.target.value }))} className="w-full text-xs border rounded px-2 py-1.5">
                {['oral','topical','intramuscular','subcutaneous','inhaled','rectal','ophthalmic','otic','nasal','sublingual','transdermal'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] text-slate-500 uppercase">Frequency</label>
              <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} className="w-full text-xs border rounded px-2 py-1.5">
                <option value="">Select...</option>
                {['once daily','twice daily','three times daily','every morning','every evening','at bedtime','every 4 hours','every 6 hours','every 8 hours','as needed','as directed'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] text-slate-500 uppercase">Quantity</label>
              <input value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className="w-full text-xs border rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="text-[9px] text-slate-500 uppercase">Refills</label>
              <input value={form.refills_total} onChange={e => setForm(f => ({ ...f, refills_total: e.target.value }))} className="w-full text-xs border rounded px-2 py-1.5" />
            </div>
          </div>
          <div>
            <label className="text-[9px] text-slate-500 uppercase">Diagnosis (ICD-10) *</label>
            <input value={form.linked_diagnosis} onChange={e => setForm(f => ({ ...f, linked_diagnosis: e.target.value }))}
              placeholder="e.g., E11.9, I10" className="w-full text-xs border rounded px-2 py-1.5 font-mono" data-testid="med-dx" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={!form.linked_diagnosis || submitting}
              className="gap-1" data-testid="med-save-btn">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Add to Med List
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   RX PAD — Prescription Writing Form (8C)
   ═══════════════════════════════════════ */



/* ═══════════════════════════════════════
   PENDING RX SECTION — Draft prescriptions needing signature
   ═══════════════════════════════════════ */

function PendingRxSection({ pendingRx, patientId, onChanged }) {
  const [signing, setSigning] = useState(null);
  const [editingRx, setEditingRx] = useState(null); // rx id being edited

  const handleSign = async (rxId) => {
    setSigning(rxId);
    try {
      await api.post(`/prescriptions/${rxId}/sign`);
      toast.success('Prescription signed');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sign failed'); }
    finally { setSigning(null); }
  };

  const handleSignAndSend = async (rxId) => {
    setSigning(rxId);
    try {
      await api.post(`/prescriptions/${rxId}/sign`);
      await api.post(`/prescriptions/${rxId}/transmit`);
      toast.success('Signed & sent to pharmacy');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSigning(null); }
  };

  const handleCancel = async (rxId) => {
    try {
      await api.post(`/prescriptions/${rxId}/cancel`, { reason: 'Cancelled by provider' });
      toast.success('Prescription cancelled');
      onChanged();
    } catch { toast.error('Cancel failed'); }
  };

  return (
    <div className="rounded-xl border-2 border-amber-200 bg-amber-50/30 p-3 space-y-2" data-testid="pending-rx-section">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-600" />
        <span className="text-xs font-semibold text-amber-800">Pending Prescriptions ({pendingRx.length})</span>
      </div>
      {pendingRx.map(rx => (
        <div key={rx.id}>
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-amber-100">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-700">{rx.medication_name} {rx.strength}</span>
                {rx.is_controlled && <Badge className="text-[7px] bg-red-100 text-red-600">C-{rx.controlled_schedule}</Badge>}
                <Badge variant="outline" className="text-[8px] bg-amber-50 text-amber-600 border-amber-200">draft</Badge>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">{rx.sig} · Qty {rx.quantity} · {rx.pharmacy_name || 'No pharmacy'}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" className="h-6 text-[9px] gap-0.5 rounded-full text-slate-500"
                onClick={() => setEditingRx(editingRx === rx.id ? null : rx.id)} data-testid={`edit-rx-${rx.id}`}>
                <Edit3 className="w-2.5 h-2.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="h-6 text-[9px] gap-0.5 rounded-full text-emerald-600 border-emerald-200"
                onClick={() => handleSign(rx.id)} disabled={signing === rx.id}>
                {signing === rx.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" />} Sign
              </Button>
              <Button size="sm" className="h-6 text-[9px] gap-0.5 rounded-full bg-blue-600 hover:bg-blue-700"
                onClick={() => handleSignAndSend(rx.id)} disabled={signing === rx.id}>
                Sign & Send
              </Button>
              <Button size="sm" variant="ghost" className="h-6 text-[9px] text-slate-400 rounded-full"
                onClick={() => handleCancel(rx.id)}>
                <X className="w-2.5 h-2.5" />
              </Button>
            </div>
          </div>
          {/* Inline edit form */}
          {editingRx === rx.id && (
            <PendingRxEditForm rx={rx} onSaved={() => { setEditingRx(null); onChanged(); }} onCancel={() => setEditingRx(null)} />
          )}
        </div>
      ))}
    </div>
  );
}

function PendingRxEditForm({ rx, onSaved, onCancel }) {
  const [med, setMed] = useState(rx.medication_name || '');
  const [strength, setStrength] = useState(rx.strength || '');
  const [sig, setSig] = useState(rx.sig || '');
  const [qty, setQty] = useState(rx.quantity || '');
  const [refills, setRefills] = useState(rx.refills ?? 0);
  const [pharmacy, setPharmacy] = useState(rx.pharmacy_name || '');
  const [notes, setNotes] = useState(rx.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/prescriptions/${rx.id}`, {
        medication_name: med,
        strength,
        sig,
        quantity: String(qty),
        refills: Number(refills),
        pharmacy_name: pharmacy,
        notes,
      });
      toast.success('Prescription updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-3 mt-1 mb-1 p-3 rounded-lg border border-blue-200 bg-blue-50/30 space-y-2" data-testid={`edit-rx-form-${rx.id}`}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[9px] text-slate-500 uppercase font-semibold">Medication</label>
          <input value={med} onChange={e => setMed(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md bg-white" />
        </div>
        <div>
          <label className="text-[9px] text-slate-500 uppercase font-semibold">Strength</label>
          <input value={strength} onChange={e => setStrength(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md bg-white" />
        </div>
      </div>
      <div>
        <label className="text-[9px] text-slate-500 uppercase font-semibold">Sig (Directions)</label>
        <input value={sig} onChange={e => setSig(e.target.value)}
          className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md bg-white" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[9px] text-slate-500 uppercase font-semibold">Qty</label>
          <input value={qty} onChange={e => setQty(e.target.value)} type="number"
            className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md bg-white" />
        </div>
        <div>
          <label className="text-[9px] text-slate-500 uppercase font-semibold">Refills</label>
          <input value={refills} onChange={e => setRefills(e.target.value)} type="number" min="0"
            className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md bg-white" />
        </div>
        <div>
          <label className="text-[9px] text-slate-500 uppercase font-semibold">Pharmacy</label>
          <input value={pharmacy} onChange={e => setPharmacy(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md bg-white" />
        </div>
      </div>
      <div>
        <label className="text-[9px] text-slate-500 uppercase font-semibold">Notes</label>
        <input value={notes} onChange={e => setNotes(e.target.value)}
          className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md bg-white" placeholder="Optional notes" />
      </div>
      <div className="flex gap-1.5 justify-end">
        <Button size="sm" variant="ghost" className="h-6 text-[9px]" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="h-6 text-[9px] gap-0.5" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" />} Save Changes
        </Button>
      </div>
    </div>
  );
}


function RxPadForm({ patientId, onWritten, onClose }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pharmacySearch, setPharmacySearch] = useState('');
  const [pharmacyResults, setPharmacyResults] = useState([]);
  const [patientPharmacies, setPatientPharmacies] = useState([]);
  const [form, setForm] = useState({
    strength: '', form: 'tablet', route: 'oral', sig: '', quantity: '', refills: 0,
    daw: false, linked_diagnosis: '', pharmacy_id: '', pharmacy_name: '', notes_to_pharmacy: '', rx_type: 'new',
  });
  const [submitting, setSubmitting] = useState(false);
  const [sigParts, setSigParts] = useState({ verb: 'Take', qty: '1', formWord: 'tablet', route: 'by mouth', freq: 'daily' });

  // Load patient's recent pharmacies
  useEffect(() => {
    api.get(`/patients/${patientId}/pharmacies`).then(r => setPatientPharmacies(r.data.pharmacies || [])).catch(() => {});
  }, [patientId]);

  // Med search
  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    api.get(`/medications/search?q=${search}&limit=8`).then(r => setResults(r.data.medications || [])).catch(() => {});
  }, [search]);

  // Pharmacy search
  useEffect(() => {
    if (pharmacySearch.length < 1) { setPharmacyResults([]); return; }
    api.get(`/pharmacies/search?q=${pharmacySearch}`).then(r => setPharmacyResults(r.data.pharmacies || [])).catch(() => {});
  }, [pharmacySearch]);

  const selectMed = async (med) => {
    setSelected(med); setSearch(''); setResults([]);
    try {
      const res = await api.get(`/medications/${encodeURIComponent(med.name)}/defaults`);
      const d = res.data.defaults;
      const defaultStrength = d.selected_strength || med.strengths?.[0] || '';
      setForm(f => ({
        ...f, strength: defaultStrength, form: d.form?.toLowerCase() || 'tablet', route: d.route || 'oral',
        sig: d.default_sig || '', quantity: d.default_qty?.toString() || '30', refills: d.default_refills || 0,
      }));
      setSigParts(p => ({ ...p, formWord: d.form?.toLowerCase() || 'tablet' }));
    } catch {}
  };

  const buildSig = () => `${sigParts.verb} ${sigParts.qty} ${sigParts.formWord} ${sigParts.route} ${sigParts.freq}`;

  const selectPharmacy = (ph) => {
    setForm(f => ({ ...f, pharmacy_id: ph.pharmacy_id || ph.id, pharmacy_name: ph.pharmacy_name || ph.name }));
    setPharmacySearch(''); setPharmacyResults([]);
  };

  const handleSubmit = async (signImmediately = false) => {
    if (!selected || !form.linked_diagnosis) return;
    setSubmitting(true);
    try {
      const sig = form.sig || buildSig();
      const res = await api.post(`/patients/${patientId}/prescriptions`, {
        medication_name: selected.name, ...form, sig, dose: form.strength,
      });
      if (res.data.allergy_warning?.match) {
        toast.error(res.data.allergy_warning.message, { duration: 8000 });
      }
      if (signImmediately && res.data.prescription?.id) {
        await api.post(`/prescriptions/${res.data.prescription.id}/sign`);
        toast.success(`${selected.name} — signed`);
      } else {
        toast.success(`${selected.name} — draft saved`);
      }
      onWritten();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-xl border-2 border-blue-300 bg-blue-50/20 p-5 space-y-4" data-testid="rx-pad">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-blue-800 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
          <Edit3 className="w-4 h-4" /> Rx Pad
        </h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-blue-100"><X className="w-4 h-4 text-blue-400" /></button>
      </div>

      {/* Step 1: Medication Search */}
      {!selected ? (
        <div>
          <label className="text-[10px] text-slate-500 uppercase font-medium">Medication</label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search medication name..." autoFocus
              className="w-full pl-10 pr-3 py-2.5 text-sm border border-blue-200 rounded-xl bg-white" data-testid="rx-med-search" />
          </div>
          {results.length > 0 && (
            <div className="border rounded-xl mt-1.5 max-h-48 overflow-y-auto shadow-sm bg-white">
              {results.map(med => (
                <button key={med.name} onClick={() => selectMed(med)}
                  className="w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50 border-b last:border-0 flex items-center gap-2">
                  <Pill className="w-3.5 h-3.5 text-blue-400" />
                  <span className="font-medium">{med.name}</span>
                  {med.strengths?.length > 0 && <span className="text-slate-400">({med.strengths.join(', ')})</span>}
                  <span className="text-slate-300 ml-auto text-[9px]">{med.common_indication}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Selected medication */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-100/50 border border-blue-200">
            <Pill className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-bold text-blue-800">{selected.name}</span>
            {selected.strengths && (
              <select value={form.strength} onChange={e => setForm(f => ({ ...f, strength: e.target.value }))}
                className="text-xs border border-blue-200 rounded px-1.5 py-0.5 bg-white font-mono">
                {selected.strengths.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <button onClick={() => setSelected(null)} className="text-[9px] text-blue-500 ml-auto">change</button>
          </div>

          {/* Sig Builder */}
          <div>
            <label className="text-[10px] text-slate-500 uppercase font-medium">Sig (Instructions)</label>
            <input type="text" value={form.sig} onChange={e => setForm(f => ({ ...f, sig: e.target.value }))}
              placeholder={buildSig()} className="w-full mt-1 text-sm border rounded-xl px-3 py-2" />
          </div>

          {/* Quantity + Refills + DAW */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-medium">Quantity</label>
              <input value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                className="w-full mt-1 text-sm border rounded-lg px-2.5 py-1.5 text-center font-mono" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-medium">Refills</label>
              <input type="number" min="0" max="99" value={form.refills} onChange={e => setForm(f => ({ ...f, refills: parseInt(e.target.value) || 0 }))}
                className="w-full mt-1 text-sm border rounded-lg px-2.5 py-1.5 text-center font-mono" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase font-medium">Form</label>
              <select value={form.form} onChange={e => setForm(f => ({ ...f, form: e.target.value }))} className="w-full mt-1 text-xs border rounded-lg px-2 py-1.5">
                {['tablet', 'capsule', 'liquid', 'cream', 'injection', 'inhaler', 'patch', 'drops', 'spray'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.daw} onChange={e => setForm(f => ({ ...f, daw: e.target.checked }))} className="rounded" />
                <span className="text-[10px] font-medium text-slate-600">DAW</span>
              </label>
            </div>
          </div>

          {/* Diagnosis */}
          <div>
            <label className="text-[10px] text-slate-500 uppercase font-medium">Diagnosis (ICD-10) *</label>
            <input value={form.linked_diagnosis} onChange={e => setForm(f => ({ ...f, linked_diagnosis: e.target.value }))}
              placeholder="e.g., E11.9, I10" className="w-full mt-1 text-sm border rounded-lg px-2.5 py-1.5 font-mono" data-testid="rx-dx" />
          </div>

          {/* Pharmacy — Patient's recent + search */}
          <div>
            <label className="text-[10px] text-slate-500 uppercase font-medium">Pharmacy</label>
            {form.pharmacy_name ? (
              <div className="flex items-center gap-2 mt-1 px-3 py-2 rounded-lg bg-slate-50 border">
                <span className="text-xs font-medium">{form.pharmacy_name}</span>
                <button onClick={() => setForm(f => ({ ...f, pharmacy_id: '', pharmacy_name: '' }))} className="text-[9px] text-blue-500 ml-auto">change</button>
              </div>
            ) : (
              <div className="mt-1 space-y-1.5">
                {/* Patient's recent pharmacies as quick-picks */}
                {patientPharmacies.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {patientPharmacies.map(ph => (
                      <button key={ph.id} onClick={() => selectPharmacy(ph)}
                        className="px-2.5 py-1 rounded-full border border-blue-200 text-[10px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                        data-testid={`quick-pharmacy-${ph.pharmacy_name}`}>
                        {ph.pharmacy_name}
                      </button>
                    ))}
                  </div>
                )}
                <input type="text" value={pharmacySearch} onChange={e => setPharmacySearch(e.target.value)}
                  placeholder="Search pharmacy by name..." className="w-full text-xs border rounded-lg px-2.5 py-1.5" />
                {pharmacyResults.length > 0 && (
                  <div className="border rounded-lg max-h-32 overflow-y-auto">
                    {pharmacyResults.map(ph => (
                      <button key={ph.id} onClick={() => selectPharmacy(ph)}
                        className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 border-b last:border-0">
                        <span className="font-medium">{ph.name}</span>
                        <span className="text-slate-400 ml-2">{ph.address}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions — unified: Add to Med List, Save Draft, or Sign Rx */}
          <div className="flex justify-end gap-2 pt-2 border-t border-blue-200">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">Cancel</Button>
            <Button variant="outline" size="sm" onClick={async () => {
              if (!selected || !form.linked_diagnosis) return;
              setSubmitting(true);
              try {
                await api.post(`/patients/${patientId}/active-medications`, {
                  medication_name: selected.name, dose: form.strength, form: form.form, route: form.route,
                  frequency: '', sig: form.sig || buildSig(), quantity: form.quantity,
                  refills_total: form.refills, linked_diagnosis: form.linked_diagnosis,
                  pharmacy_name: form.pharmacy_name,
                });
                toast.success(`${selected.name} added to med list`);
                onWritten();
              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
              finally { setSubmitting(false); }
            }} disabled={!form.linked_diagnosis || submitting}
              className="text-xs gap-1 border-slate-200 text-slate-600" data-testid="rx-add-medlist">
              <Plus className="w-3 h-3" /> Add to Med List
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleSubmit(false)} disabled={!form.linked_diagnosis || submitting}
              className="text-xs gap-1 border-blue-200 text-blue-600" data-testid="rx-save-draft">
              Save Draft Rx
            </Button>
            <Button size="sm" onClick={() => handleSubmit(true)} disabled={!form.linked_diagnosis || submitting}
              className="text-xs gap-1 bg-blue-600 hover:bg-blue-700" data-testid="rx-sign-btn">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Sign & Send
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   RX HISTORY PANEL (8C)
   ═══════════════════════════════════════ */

function RxHistoryPanel({ prescriptions, onClose, patientId }) {
  const [tab, setTab] = useState('table');
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const RX_STATUS_COLORS = {
    draft: 'bg-slate-100 text-slate-500', signed: 'bg-blue-50 text-blue-600',
    transmitted: 'bg-violet-50 text-violet-600', accepted: 'bg-sky-50 text-sky-600',
    filled: 'bg-emerald-50 text-emerald-600',
    rejected: 'bg-red-50 text-red-600', cancelled: 'bg-slate-100 text-slate-400',
  };

  const ACTION_LABELS = {
    prescription_written: 'Written', prescription_signed: 'Signed',
    prescription_transmitted: 'Sent to Pharmacy', prescription_cancelled: 'Cancelled',
  };

  const loadTimeline = async () => {
    if (!patientId) return;
    setLoadingTimeline(true);
    try {
      const res = await api.get(`/patients/${patientId}/prescription-timeline`);
      setTimeline(res.data.timeline || []);
    } catch {} finally { setLoadingTimeline(false); }
  };

  useEffect(() => { if (tab === 'timeline') loadTimeline(); }, [tab]); // eslint-disable-line

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/15" />
      <div className="relative w-full max-w-3xl max-h-[75vh] bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Prescription History</h3>
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {[{ key: 'table', label: 'Table' }, { key: 'timeline', label: 'Timeline' }].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-2.5 py-1 text-[10px] font-medium rounded-md ${tab === t.key ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === 'table' ? (
            /* Table view */
            prescriptions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-12">No prescriptions written</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 text-[9px] text-slate-400 uppercase">Date</th>
                    <th className="text-left px-2 py-2 text-[9px] text-slate-400 uppercase">Medication</th>
                    <th className="text-left px-2 py-2 text-[9px] text-slate-400 uppercase">Sig</th>
                    <th className="text-left px-2 py-2 text-[9px] text-slate-400 uppercase">Qty</th>
                    <th className="text-left px-2 py-2 text-[9px] text-slate-400 uppercase">Pharmacy</th>
                    <th className="text-left px-2 py-2 text-[9px] text-slate-400 uppercase">Status</th>
                    <th className="text-left px-2 py-2 text-[9px] text-slate-400 uppercase">Prescriber</th>
                  </tr>
                </thead>
                <tbody>
                  {prescriptions.map(rx => (
                    <tr key={rx.id} className={`border-b hover:bg-slate-50 ${rx.is_controlled ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">{new Date(rx.written_at).toLocaleDateString()}</td>
                      <td className="px-2 py-2.5">
                        <span className="font-medium text-slate-700">{rx.medication_name} {rx.strength}</span>
                        {rx.is_controlled && <Badge className="ml-1 text-[7px] bg-red-100 text-red-600 border-red-200">C-{rx.controlled_schedule}</Badge>}
                      </td>
                      <td className="px-2 py-2.5 text-slate-500 max-w-[160px] truncate">{rx.sig}</td>
                      <td className="px-2 py-2.5 text-slate-500 tabular-nums">{rx.quantity}</td>
                      <td className="px-2 py-2.5 text-slate-500">{rx.pharmacy_name || '—'}</td>
                      <td className="px-2 py-2.5"><Badge variant="outline" className={`text-[8px] ${RX_STATUS_COLORS[rx.status] || ''}`}>{rx.status}</Badge></td>
                      <td className="px-2 py-2.5 text-slate-500">{rx.prescriber_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            /* Timeline view */
            loadingTimeline ? (
              <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
            ) : timeline.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-12">No prescription events</p>
            ) : (
              <div className="px-6 py-4">
                <div className="relative border-l-2 border-slate-200 ml-3 space-y-4">
                  {timeline.map(event => (
                    <div key={event.id} className="relative pl-6">
                      {/* Timeline dot */}
                      <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${
                        event.action === 'prescription_signed' ? 'bg-blue-500' :
                        event.action === 'prescription_transmitted' ? 'bg-violet-500' :
                        event.action === 'prescription_cancelled' ? 'bg-red-400' :
                        'bg-slate-300'
                      }`} />
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-slate-700">
                              {event.medication_name} {event.strength}
                            </span>
                            {event.is_controlled && <Badge className="text-[7px] bg-red-100 text-red-600 border-red-200">Controlled</Badge>}
                            <Badge variant="outline" className={`text-[8px] ${RX_STATUS_COLORS[event.status] || ''}`}>
                              {ACTION_LABELS[event.action] || event.action?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-slate-400">{event.user_name}</span>
                            {event.pharmacy && <span className="text-[10px] text-slate-400">→ {event.pharmacy}</span>}
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-300 tabular-nums shrink-0">
                          {new Date(event.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

